import { DayPickerProps, DayPicker } from 'react-day-picker';
import { enUS, pl, ru, uk } from 'react-day-picker/locale';
import { DateTime } from 'luxon';

import { typographyVariants, Typography } from '../typography/typography';
import { buttonVariants } from '../button/button';
import { cn } from '../../utils';

export type CalendarProps = DayPickerProps;

const calendarLocales = { en: enUS, pl: pl, ru: ru, uk: uk };

function Calendar({
  showOutsideDays = false,
  classNames,
  className,
  lang,
  ...props
}: DayPickerProps) {
  const selectedLanguage = lang || 'pl';
  const locale = calendarLocales[selectedLanguage as keyof typeof calendarLocales];

  return (
    <DayPicker
      classNames={{
        months: 'flex flex-col sm:flex-row gap-y-4 sm:gap-x-4 sm:gap-y-0',
        month: 'flex flex-col gap-y-2',
        month_caption: 'flex items-center justify-center h-10',
        dropdowns: 'flex gap-2',
        dropdown: 'flex',
        caption_label: 'text-sm font-medium',
        nav: 'absolute left-4 right-4 top-4 flex items-center justify-between',
        button_previous: cn(
          buttonVariants({ variant: 'ghost', size: 'small' }),
          'bg-transparent opacity-50 hover:opacity-100 z-10',
        ),
        button_next: cn(
          buttonVariants({ variant: 'ghost', size: 'small' }),
          'bg-transparent opacity-50 hover:opacity-100 z-10',
        ),
        month_grid: 'w-full border-collapse space-y-1',
        weekdays: 'invisible flex space-x-1',
        weekday: 'text-weak rounded-md font-normal text-[0.8rem] w-8',
        week: 'flex w-full gap-1',
        weeks: 'flex flex-col gap-1',
        day: cn(
          'relative p-0 text-center text-default text-sm focus-within:relative focus-within:z-20 aria-selected:bg-surface-weak aria-selected:[&.day-outside]:bg-weak/50',
          props.mode === 'range'
            ? '[&.day-range-end]:rounded-r-md [&.day-range-start]:rounded-l-full aria-selected:[&.day-range-end]:rounded-r-full first:aria-selected:rounded-l-full last:aria-selected:rounded-r-full'
            : 'aria-selected:rounded-full',
          typographyVariants({ variant: 'labelMediumBook' }),
        ),
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'text-default size-8 p-0 font-normal rounded-full',
        ),
        range_start:
          'day-range-start before:absolute before:-right-0.5 before:top-0 before:w-0.5 before:bg-surface-weak before:bottom-0 [&.day-outside]:before:bg-weak/50',
        range_end:
          'day-range-end before:absolute [&.day-range-start]:before:bg-transparent before:-left-0.5 before:top-0 before:w-0.5 before:bg-surface-weak before:bottom-0 [&.day-outside]:before:bg-weak/50',
        selected:
          'day-selected *:bg-surface *:!text-contrast *:hover:bg-surface *:hover:text-contrast *:focus:bg-surface *:focus:text-contrast *:opacity-100',
        today: '*:text-surface',
        outside:
          'day-outside *:text-weak *:opacity-50 *:aria-selected:bg-weak/50 *:aria-selected:text-weak *:aria-selected:opacity-30',
        disabled: 'text-weak/30 opacity-50',
        range_middle:
          'day-range-middle *:aria-selected:bg-red-600 *:aria-selected:text-surface w-9 -mx-0.5 [&>button]:bg-transparent [&>button]:text-weak',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        // eslint-disable-next-line react/prop-types
        CaptionLabel: ({ children }) => (
          <Typography variant="labelMediumBold">{children} </Typography>
        ),
      }}
      startMonth={DateTime.now().minus({ years: 5 }).toJSDate()}
      endMonth={DateTime.now().plus({ years: 5 }).toJSDate()}
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      defaultMonth={new Date()}
      captionLayout="label"
      locale={locale}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
